/**
 * One department rule (D1 wave 2, workspace docs/guardian/05, 2026-10-22):
 * what it says, the exact source passage it came from, and the review
 * actions. All document and rule text is rendered as text, never HTML; the
 * quoted span is highlighted by slicing the chunk text at the Backend's offsets.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { fetchDepartmentRule } from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';

export const RULE_TYPES = [
  ['obligation', 'Must do'],
  ['prohibition', 'Must not do'],
  ['permission', 'May do'],
  ['threshold', 'Limit or trigger value'],
  ['trigger', 'Starts when'],
  ['definition', 'Definition'],
];

export const PRIORITIES = [
  [3, 'Life safety'],
  [2, 'Core operations'],
  [1, 'Routine'],
  [0, 'Administrative'],
];

const FLAG_TEXT = {
  url: 'contains a web address',
  override: 'reads like an instruction to ignore other rules',
  persona: 'reads like an instruction to an assistant',
  prompt: 'mentions prompts',
  assistant: 'mentions an AI assistant',
  tool: 'mentions tool calls',
  markup: 'contains markup',
  // Ingest red-team 2026-10-23 (Interlinked-Backend F1/F2).
  hidden_text: 'contains hidden characters',
  quote_repeated: 'quotes a phrase that appears more than once in the passage, so check it is the right one',
  number_not_in_source: 'has a number that is not in the source text',
  // D1 gate run 4 (2026-11-03, Interlinked-Backend condition check).
  condition_removed: 'had a condition that is not in its source text, so it was removed: check the rule applies in every case',
  // D1 gate run 5 (2026-11-03).
  prohibition_unnegated: 'is a prohibition worded as the act itself (no "do not" or "never"): edit it to say what is forbidden',
};

export function typeLabel(type) {
  return (RULE_TYPES.find(([v]) => v === type) || [null, type])[1];
}

export function flagText(flags) {
  return (flags || []).map((f) => FLAG_TEXT[f] || f).join('; ');
}

/** The chunk text with [start, end) highlighted, as React text nodes. */
export function Highlighted({ text, start, end }) {
  const safe = typeof text === 'string' ? text : '';
  const a = Math.max(0, Math.min(start | 0, safe.length));
  const b = Math.max(a, Math.min(end | 0, safe.length));
  return (
    <Typography variant="body2" component="p" sx={{ whiteSpace: 'pre-wrap' }}>
      {safe.slice(0, a)}
      <Box component="mark" sx={{ bgcolor: 'warning.light', px: 0.25 }}>{safe.slice(a, b)}</Box>
      {safe.slice(b)}
    </Typography>
  );
}

export function RuleEditor({ initial, title, onSave, onClose }) {
  const [type, setType] = useState(initial?.type || 'obligation');
  const [condition, setCondition] = useState(initial?.condition || '');
  const [action, setAction] = useState(initial?.action || '');
  const [priority, setPriority] = useState(initial?.priority ?? 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    const err = await onSave({ type, condition: condition.trim() || null, action: action.trim(), priority });
    setBusy(false);
    if (err) setError(err);
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="rule-editor-title">
      <DialogTitle id="rule-editor-title">{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Select size="small" value={type} onChange={(e) => setType(e.target.value)} inputProps={{ 'aria-label': 'Rule type' }}>
          {RULE_TYPES.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
        </Select>
        <TextField size="small" label="When it applies (optional)" value={condition} onChange={(e) => setCondition(e.target.value)} inputProps={{ maxLength: 300 }} />
        <TextField size="small" label="What the rule says" value={action} onChange={(e) => setAction(e.target.value)} multiline minRows={2} inputProps={{ maxLength: 500 }} />
        <Select size="small" value={priority} onChange={(e) => setPriority(Number(e.target.value))} inputProps={{ 'aria-label': 'Priority' }}>
          {PRIORITIES.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !action.trim()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function RuleCard({ authToken, rule, onApprove, onReject, onRetire, onEdit }) {
  const [source, setSource] = useState(null);
  const [sourceError, setSourceError] = useState(null);
  const [ack, setAck] = useState(false);
  const [editing, setEditing] = useState(false);
  const flagged = (rule.flags || []).length > 0;
  const conflicts = (rule.conflictsWith || []).length;

  const toggleSource = async () => {
    if (source) {
      setSource(null);
      return;
    }
    const res = await fetchDepartmentRule(authToken, rule.id);
    if (res?.success && res.data?.chunk) setSource(res.data);
    else setSourceError(departmentErrorMessage(res, 'Could not load the source text.'));
  };

  return (
    <Paper variant="outlined" component="li" sx={{ p: 1.5, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Chip size="small" label={typeLabel(rule.type)} />
        <Chip size="small" variant="outlined" label={(PRIORITIES.find(([v]) => v === rule.priority) || [null, ''])[1]} />
        {conflicts > 0 && <Chip size="small" color="error" label={`Conflicts with ${conflicts} rule${conflicts > 1 ? 's' : ''}`} />}
        {flagged && <Chip size="small" color="warning" label="Check wording" />}
        {rule.supersedes && <Chip size="small" variant="outlined" label="Replaces an approved rule" />}
        {rule.origin === 'manual' && <Chip size="small" variant="outlined" label="Added by your team" />}
      </Box>
      {rule.condition && <Typography variant="caption" color="text.secondary">When: {rule.condition}</Typography>}
      <Typography variant="body1">{rule.action}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        “{rule.sourceSpan}”
        {[rule.documentTitle, rule.pageFrom ? `p. ${rule.pageFrom}` : null].filter(Boolean).length > 0 &&
          ` — ${[rule.documentTitle, rule.pageFrom ? `p. ${rule.pageFrom}` : null].filter(Boolean).join(', ')}`}
      </Typography>
      {flagged && (
        <Alert severity="warning">
          This text {flagText(rule.flags)}. Make sure it is really your department&apos;s rule before approving.
        </Alert>
      )}
      {sourceError && <Alert severity="error">{sourceError}</Alert>}
      {source && (
        <Box sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {[source.chunk.sectionPath, source.chunk.pageFrom ? `p. ${source.chunk.pageFrom}` : null].filter(Boolean).join(' · ')}
          </Typography>
          <Highlighted text={source.chunk.text} start={rule.charStart} end={rule.charEnd} />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button size="small" onClick={toggleSource}>{source ? 'Hide source' : 'Show source'}</Button>
        {rule.status === 'draft' && (
          <>
            {flagged && (
              <FormControlLabel
                control={<Checkbox size="small" checked={ack} onChange={(e) => setAck(e.target.checked)} />}
                label="I checked the wording"
              />
            )}
            <Button size="small" variant="contained" disabled={flagged && !ack} onClick={() => onApprove(rule, ack)}>Approve</Button>
            <Button size="small" onClick={() => setEditing(true)}>Edit</Button>
            <Button size="small" color="error" onClick={() => onReject(rule)}>Reject</Button>
          </>
        )}
        {rule.status === 'approved' && (
          <>
            <Button size="small" onClick={() => setEditing(true)}>Edit</Button>
            <Button size="small" color="error" onClick={() => onRetire(rule)}>Retire</Button>
          </>
        )}
      </Box>
      {editing && (
        <RuleEditor
          initial={rule}
          title={rule.status === 'approved' ? 'Edit rule (the change needs approval)' : 'Edit rule'}
          onClose={() => setEditing(false)}
          onSave={async (changes) => {
            const err = await onEdit(rule, changes);
            if (!err) setEditing(false);
            return err;
          }}
        />
      )}
    </Paper>
  );
}
